import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, ChevronDown, ChevronUp, Sparkles, Gift, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { Button } from '@/components/ui/button';

interface XPTransaction {
  id: string;
  xp_amount: number;
  reason: string;
  created_at: string;
  report_id?: string;
}

interface XPHistoryProps {
  workerId: string;
  compact?: boolean;
}

export const XPHistory = ({ workerId, compact = false }: XPHistoryProps) => {
  const [transactions, setTransactions] = useState<XPTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, [workerId]);

  const loadTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from('xp_transactions')
        .select('id, xp_amount, reason, created_at, report_id')
        .eq('worker_id', workerId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error loading XP history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (reason: string) => {
    if (reason.toLowerCase().includes('quest') || reason.toLowerCase().includes('modul')) {
      return <Gift className="h-4 w-4 text-violet-500" />;
    }
    if (reason.toLowerCase().includes('hazard') || reason.toLowerCase().includes('laporan')) {
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
    if (reason.toLowerCase().includes('streak') || reason.toLowerCase().includes('bonus')) {
      return <Sparkles className="h-4 w-4 text-amber-500" />;
    }
    if (reason.toLowerCase().includes('team') || reason.toLowerCase().includes('cluster')) {
      return <Trophy className="h-4 w-4 text-emerald-500" />;
    }
    return <CheckCircle className="h-4 w-4 text-primary" />;
  };

  const displayTransactions = showAll ? transactions : transactions.slice(0, 5);
  const totalXP = transactions.reduce((sum, t) => sum + t.xp_amount, 0);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <Trophy className="h-4 w-4" />
          Lihat Riwayat XP ({transactions.length})
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="space-y-2 pt-2">
                {displayTransactions.map((tx, idx) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                  >
                    {getIcon(tx.reason)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground truncate">{tx.reason}</p>
                      <p className="text-[10px] text-muted-foreground/60">
                        {format(new Date(tx.created_at), 'd MMM yyyy, HH:mm', { locale: id })}
                      </p>
                    </div>
                    <span className={`text-sm font-bold ${tx.xp_amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {tx.xp_amount >= 0 ? '+' : ''}{tx.xp_amount}
                    </span>
                  </motion.div>
                ))}
                
                {transactions.length > 5 && !showAll && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowAll(true)}
                    className="w-full text-xs"
                  >
                    Lihat semua ({transactions.length - 5} lagi)
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          Riwayat XP
        </h3>
        <span className="text-sm text-muted-foreground">
          Total: <span className="font-bold text-foreground">{totalXP} XP</span>
        </span>
      </div>

      {transactions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Belum ada riwayat XP</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayTransactions.map((tx, idx) => (
            <motion.div
              key={tx.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border/50"
            >
              <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center shadow-sm">
                {getIcon(tx.reason)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{tx.reason}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(tx.created_at), 'EEEE, d MMMM yyyy • HH:mm', { locale: id })}
                </p>
              </div>
              <span className={`text-lg font-bold ${tx.xp_amount >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {tx.xp_amount >= 0 ? '+' : ''}{tx.xp_amount}
              </span>
            </motion.div>
          ))}
          
          {transactions.length > 5 && !showAll && (
            <Button 
              variant="outline" 
              onClick={() => setShowAll(true)}
              className="w-full rounded-xl"
            >
              Lihat semua riwayat ({transactions.length - 5} lagi)
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default XPHistory;
